import { Building2, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useWizardStore, useWizardActions } from '@/stores';
import type { OrganizationType } from '@/types';
import { NewOrganizationForm } from './NewOrganizationForm';
import { ExistingOrganizationForm } from './ExistingOrganizationForm';

interface OrganizationTypeOptionProps {
  type: OrganizationType;
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
}

function OrganizationTypeOption({
  type,
  title,
  description,
  icon,
  selected,
  onSelect,
}: OrganizationTypeOptionProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:border-primary/50',
        selected && 'border-primary ring-2 ring-primary/20'
      )}
      onClick={onSelect}
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <CardHeader className="flex flex-row items-center gap-4 pb-2">
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-lg',
            selected ? 'bg-primary text-primary-foreground' : 'bg-muted'
          )}
        >
          {icon}
        </div>
        <div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <input
          type="radio"
          name="organizationType"
          value={type}
          checked={selected}
          onChange={() => onSelect()}
          className="sr-only"
          aria-label={title}
        />
      </CardContent>
    </Card>
  );
}

export function OrganizationTypeStep() {
  const organizationType = useWizardStore((s) => s.organizationType);
  const { setOrganizationType } = useWizardActions();

  return (
    <div className="space-y-6" role="radiogroup" aria-label="Organization type selection">
      <div className="grid gap-4 md:grid-cols-2">
        <OrganizationTypeOption
          type="New"
          title="New Organization"
          description="Create a new organization in Amplify and migrate data to it"
          icon={<PlusCircle className="h-6 w-6" />}
          selected={organizationType === 'New'}
          onSelect={() => setOrganizationType('New')}
        />

        <OrganizationTypeOption
          type="Existing"
          title="Existing Organization"
          description="Migrate data to an organization that already exists in Amplify"
          icon={<Building2 className="h-6 w-6" />}
          selected={organizationType === 'Existing'}
          onSelect={() => setOrganizationType('Existing')}
        />
      </div>

      {/* Show the appropriate form based on selection */}
      {organizationType === 'New' && (
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Organization Details</h3>
          <NewOrganizationForm />
        </div>
      )}

      {organizationType === 'Existing' && (
        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Find Organization</h3>
          <ExistingOrganizationForm />
        </div>
      )}
    </div>
  );
}
